/**
 * @techstack/core - Shared display formatters (X-16 / DASH-8)
 *
 * One home for date + currency display so the same value reads the same way
 * everywhere (Contact Inbox, Transaction drill-down, Quote Builder, GAS docs).
 * Pure functions, no framework deps — safe to import from React or plain Node.
 *
 * GAS Code.js (Apps Script) can't import npm; mirror these shapes there by hand
 * if a GAS surface needs to match.
 */

export type DateStyle =
  | 'iso' // 2026-07-08
  | 'short' // Jul 8
  | 'medium' // Jul 8, 2026
  | 'long'; // July 8, 2026

/** Coerce Date | ISO string | epoch ms into a valid Date, or null.
 *  A date-ONLY string ('YYYY-MM-DD') is parsed as LOCAL midnight, not UTC —
 *  `new Date('2025-07-01')` is UTC midnight, which renders as the previous day
 *  west of Greenwich. Timestamps with a time component keep their offset. */
function toDate(input: Date | string | number | null | undefined): Date | null {
  if (input === null || input === undefined || input === '') return null;
  if (typeof input === 'string') {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(input.trim());
    if (m) {
      const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
      return Number.isNaN(d.getTime()) ? null : d;
    }
  }
  const d = input instanceof Date ? input : new Date(input);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Format a date for display. Returns '' for null/invalid input (callers render
 * a placeholder), so this never throws on missing data.
 *
 * 'iso' uses the LOCAL calendar day (not UTC) so a timestamp near midnight
 * doesn't jump a day — matches what a user sees on their own clock.
 */
export function formatDate(
  input: Date | string | number | null | undefined,
  style: DateStyle = 'medium',
  locale?: string,
): string {
  const d = toDate(input);
  if (!d) return '';
  if (style === 'iso') {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }
  const opts: Intl.DateTimeFormatOptions =
    style === 'short'
      ? { month: 'short', day: 'numeric' }
      : style === 'long'
        ? { year: 'numeric', month: 'long', day: 'numeric' }
        : { year: 'numeric', month: 'short', day: 'numeric' };
  return d.toLocaleDateString(locale, opts);
}

export interface CurrencyOptions {
  /** Round to whole dollars (no cents). Default true — most dashboards. */
  whole?: boolean;
  /** Prefix a +/- sign (for deltas). Default false. */
  signed?: boolean;
  locale?: string;
  currency?: string;
}

/**
 * Format a number as USD. Returns '' for null/NaN so callers can placeholder.
 * whole=true → $1,234 ; whole=false → $1,234.56 ; signed=true → +$1,234 / -$1,234.
 */
export function formatCurrency(
  value: number | null | undefined,
  opts: CurrencyOptions = {},
): string {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '';
  const { whole = true, signed = false, locale = 'en-US', currency = 'USD' } = opts;
  const n = Number(value);
  const sign = signed ? (n > 0 ? '+' : n < 0 ? '-' : '') : n < 0 ? '-' : '';
  const abs = Math.abs(n);
  const body = abs.toLocaleString(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: whole ? 0 : 2,
  });
  return sign + body;
}
